import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getFirestore, doc, getDoc, updateDoc, collection, addDoc, query, where, getDocs } from 'firebase/firestore';
import { motion } from 'framer-motion';
import { getAuth } from 'firebase/auth';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import EmailService from '../../services/EmailService';
import FeedbackSurveyModal from '../../components/FeedbackSurveyModal';
import { PayoutDelayBanner } from '../../components/fraud';
import { PayoutService } from '../../services/PayoutService';
import { TRUST_TIERS } from '../../services/FraudPreventionService';

export default function PaymentConfirmation() {
  const { invoiceId } = useParams();
  const [invoiceData, setInvoiceData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const [emailSending, setEmailSending] = useState(false);
  const [emailError, setEmailError] = useState(null);
  
  // Survey state
  const [surveyStatus, setSurveyStatus] = useState(null);
  const [showSurveyModal, setShowSurveyModal] = useState(false);
  const [surveyCreating, setSurveyCreating] = useState(false);
  const [surveyError, setSurveyError] = useState(null);
  const [existingSurvey, setExistingSurvey] = useState(null);
  const [redirectCountdown, setRedirectCountdown] = useState(null);

  // Payout clearance state
  const [payoutInfo, setPayoutInfo] = useState(null);

  useEffect(() => {
    const fetchInvoiceAndSendEmails = async () => {
      try {
        console.log('🔄 PaymentConfirmation: Starting to fetch invoice and send emails');
        console.log('📋 Invoice ID:', invoiceId);
        
        const firestore = getFirestore();
        const invoiceRef = doc(firestore, 'invoices', invoiceId);
        const snap = await getDoc(invoiceRef);

        if (!snap.exists()) {
          console.log('❌ Invoice not found:', invoiceId);
          setError('Invoice not found');
        } else {
          const data = snap.data();
          console.log('✅ Invoice data fetched:', {
            id: invoiceId,
            status: data.status,
            hasPaymentEmailSettings: Boolean(data.paymentEmailSettings),
            paymentEmailsSent: data.paymentEmailsSent,
            client: data.client,
            project: data.project,
            hasSurveySettings: Boolean(data.surveySettings),
            surveyCreateEnabled: data.surveySettings?.createSurvey
          });
          
          setInvoiceData(data);
          
          // Check email settings
          const emailSettings = data.paymentEmailSettings || {};
          console.log('📧 Payment email settings:', emailSettings);
          
          setEmailStatus({
            confirmationEnabled: emailSettings.sendConfirmationEmail || false,
            style: emailSettings.emailMode || 'professional',
            customMessage: emailSettings.customMessage || '',
            notifyCreator: emailSettings.notifyCreator || false,
            sent: data.paymentEmailsSent || false,
            sentAt: data.paymentEmailsSentAt || null
          });

          // Check for existing surveys related to this invoice
          await checkExistingSurvey(data.userId, data.client, data.email);

          // Send payment confirmation emails if not already sent and enabled
          // Note: We check for 'paid' status OR if status was just updated to paid
          if (!data.paymentEmailsSent && emailSettings.sendConfirmationEmail) {
            console.log('🚀 Conditions met for sending payment emails - starting email process');
            await sendPaymentConfirmationEmails(data, emailSettings, invoiceRef);
          } else {
            console.log('⏭️ Skipping email sending:', {
              alreadySent: data.paymentEmailsSent,
              emailsEnabled: emailSettings.sendConfirmationEmail,
              currentStatus: data.status
            });
          }
          
          // Update invoice status to paid if not already (for authenticated users only)
          // For unauthenticated users, the server will handle this
          try {
            if (data.status !== 'paid') {
              console.log('💰 Updating invoice status to paid');
              await updateDoc(invoiceRef, {
                status: 'paid',
                paidAt: new Date(),
                updatedAt: new Date()
              });
              console.log('✅ Invoice status updated to paid');
            } else {
              console.log('ℹ️ Invoice already marked as paid');
            }
          } catch (authError) {
            console.log('⚠️ Could not update invoice status (likely unauthenticated user):', authError.message);
            // This is expected for unauthenticated users
          }

          // Trigger payout clearance and risk evaluation
          try {
            const payoutSvc = new PayoutService();
            const paymentAmount = parseFloat(data.total || data.amount || 0);
            if (data.userId && paymentAmount > 0) {
              const result = await payoutSvc.onPaymentCaptured(data.userId, invoiceId, paymentAmount);
              const tierConfig = Object.values(TRUST_TIERS).find(t => t.id === result.clearance.trustTier) || TRUST_TIERS.TIER_0;
              setPayoutInfo({
                delayDays: result.clearance.payoutDelayDays,
                tierName: tierConfig.name,
                riskLevel: result.riskResult.level,
                status: result.clearance.status,
              });
            }
          } catch (payoutError) {
            console.log('⚠️ Payout clearance processing note:', payoutError.message);
            // Non-blocking — payout clearance is supplementary
          }
        }
      } catch (error) {
        console.error('❌ Error in fetchInvoiceAndSendEmails:', error);
        setError(error.message);
      } finally {
        console.log('🏁 PaymentConfirmation: Process completed');
        setIsLoading(false);
      }
    };

    if (invoiceId) {
      fetchInvoiceAndSendEmails();
    } else {
      console.log('❌ No invoice ID provided');
      setError('No invoice ID provided');
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  const checkExistingSurvey = async (userId, clientName, clientEmail) => {
    try {
      const firestore = getFirestore();
      const surveysRef = collection(firestore, 'surveys');
      const q = query(
        surveysRef,
        where('userId', '==', userId),
        where('invoiceId', '==', invoiceId)
      );
      
      const querySnapshot = await getDocs(q);
      if (!querySnapshot.empty) {
        const surveyData = querySnapshot.docs[0].data();
        const surveyInfo = {
          id: querySnapshot.docs[0].id,
          ...surveyData
        };
        
        setExistingSurvey(surveyInfo);
        setSurveyStatus({
          created: true,
          sent: true,
          sentAt: surveyData.createdAt
        });
        
        // Auto-redirect to survey after 8 seconds if one exists
        console.log('📝 Survey found, setting up auto-redirect in 8 seconds');
        let countdown = 8;
        setRedirectCountdown(countdown);
        
        const countdownInterval = setInterval(() => {
          countdown--;
          setRedirectCountdown(countdown);
          
          if (countdown <= 0) {
            clearInterval(countdownInterval);
            console.log('🔄 Redirecting to survey:', surveyInfo.id);
            window.location.href = `/survey-response/${surveyInfo.id}`;
          }
        }, 1000);
        
      } else {
        // Initialize survey status for potential creation
        setSurveyStatus({
          created: false,
          sent: false,
          clientName: clientName,
          clientEmail: clientEmail
        });
      }
    } catch (error) {
      console.error('Error checking existing survey:', error);
    }
  };

  // Note: checkAndActivateSurvey function removed since surveys are now created as 'active' immediately during invoice creation

  const sendPaymentConfirmationEmails = async (invoiceData, emailSettings, invoiceRef) => {
    if (emailSending) {
      console.log('⚠️ Email sending already in progress, skipping duplicate request');
      return; // Prevent duplicate sends
    }
    
    console.log('📧 Starting payment confirmation email process');
    console.log('📊 Email settings:', emailSettings);
    console.log('📋 Invoice data summary:', {
      id: invoiceId,
      client: invoiceData.client,
      email: invoiceData.email,
      project: invoiceData.project,
      amount: invoiceData.amount || invoiceData.total
    });
    
    setEmailSending(true);
    setEmailError(null);
    
    try {
      console.log('🌐 Triggering server-side email sending...');
      
      // Use server-side trigger instead of direct email sending
      // This works without authentication since it's a public endpoint
      const result = await EmailService.triggerPaymentConfirmationEmails(invoiceId);
      
      console.log('✅ Server responded with:', result);

      // Mark emails as sent in Firestore (for authenticated users only)
      // The server will also handle this for unauthenticated users
      try {
        console.log('💾 Updating Firestore to mark emails as sent...');
        await updateDoc(invoiceRef, {
          paymentEmailsSent: true,
          paymentEmailsSentAt: new Date()
        });
        console.log('✅ Firestore updated successfully');
      } catch (authError) {
        // Ignore auth errors for unauthenticated users
        console.log('⚠️ Could not update Firestore (likely unauthenticated user):', authError.message);
      }

      // Update local state
      setEmailStatus(prev => ({
        ...prev,
        sent: true,
        sentAt: new Date().toISOString(),
        error: null
      }));
      
      console.log('🎉 Payment confirmation email process completed successfully');
      
    } catch (error) {
      console.error('❌ Payment confirmation email process failed:', error);
      console.error('🔍 Error details:', {
        message: error.message,
        stack: error.stack,
        invoiceId: invoiceId
      });
      
      setEmailError(error.message);
      setEmailStatus(prev => ({
        ...prev,
        sent: false,
        error: error.message
      }));
    } finally {
      console.log('🏁 Email sending process finished');
      setEmailSending(false);
    }
  };

  const handleCreateSurvey = async (surveyData) => {
    setSurveyCreating(true);
    setSurveyError(null);
    
    try {
      const firestore = getFirestore();
      const auth = getAuth(); // eslint-disable-line no-unused-vars
      
      // Create the survey document
      const surveyDoc = {
        ...surveyData,
        userId: invoiceData.userId,
        invoiceId: invoiceId,
        clientName: invoiceData.client || invoiceData.clientName,
        clientEmail: invoiceData.email,
        projectName: invoiceData.project || invoiceData.projectName,
        status: 'active',
        createdAt: new Date(),
        updatedAt: new Date(),
        responses: [],
        responseCount: 0
      };

      const docRef = await addDoc(collection(firestore, 'surveys'), surveyDoc);
      console.log('✅ Survey created with ID:', docRef.id);

      // Update survey status
      setSurveyStatus({
        created: true,
        sent: true,
        sentAt: new Date().toISOString(),
        surveyId: docRef.id
      });

      setExistingSurvey({
        id: docRef.id,
        ...surveyDoc
      });

      setShowSurveyModal(false);
      
    } catch (error) {
      console.error('❌ Error creating survey:', error);
      setSurveyError(error.message);
    } finally {
      setSurveyCreating(false);
    }
  };

  const handleSurveyPrompt = () => {
    setShowSurveyModal(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] dark:bg-[#0A0A0A] flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-6 h-6 border-2 border-brand border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-gray-500 dark:text-gray-400">Processing confirmation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] dark:bg-[#0A0A0A] flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-sm w-full bg-white dark:bg-[#111111] rounded-2xl p-8 text-center shadow-sm border border-gray-100 dark:border-[#1F1F1F]"
        >
          <div className="w-12 h-12 mx-auto mb-4 bg-red-50 dark:bg-red-500/10 rounded-xl flex items-center justify-center">
            <svg className="w-6 h-6 text-red-500 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Something went wrong</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">{error}</p>
          <motion.button
            onClick={() => window.location.reload()}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full py-2.5 px-4 bg-brand hover:bg-brand-hover text-white font-medium rounded-xl transition-all text-sm"
          >
            Try Again
          </motion.button>
        </motion.div>
      </div>
    );
  }

  if (!invoiceData) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] dark:bg-[#0A0A0A] flex items-center justify-center p-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-sm w-full bg-white dark:bg-[#111111] rounded-2xl p-8 text-center shadow-sm border border-gray-100 dark:border-[#1F1F1F]"
        >
          <div className="w-12 h-12 mx-auto mb-4 bg-gray-100 dark:bg-white/5 rounded-xl flex items-center justify-center">
            <svg className="w-6 h-6 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h1 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Invoice not found</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">We couldn't locate the invoice you're looking for.</p>
          <motion.button
            onClick={() => window.history.back()}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full py-2.5 px-4 bg-brand hover:bg-brand-hover text-white font-medium rounded-xl transition-all text-sm"
          >
            Go back
          </motion.button>
        </motion.div>
      </div>
    );
  }

  // Format date for receipt
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Extract email from string if needed
  const getEmailFromString = (emailStr) => {
    if (!emailStr) return '';
    const angleEmailMatch = emailStr.match(/<([^>]+)>/);
    if (angleEmailMatch) {
      return angleEmailMatch[1];
    }
    const plainEmailMatch = emailStr.match(/([a-zA-Z0-9._-]+@[a-zA-Z0-9._-]+\.[a-zA-Z0-9._-]+)/);
    return plainEmailMatch ? plainEmailMatch[1] : emailStr;
  };

  // Generate a clean, well-fitted PDF receipt
  const generateReceiptPDF = () => {
    try {
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: 'a4',
        compress: true,
        putOnlyUsedFonts: true,
      });
      doc.setFont('helvetica');
      doc.setLanguage('en-US');

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 48;
      const contentWidth = pageWidth - 2 * margin;

      // Palette
      const ink = [17, 17, 17];
      const subtle = [115, 115, 115];
      const muted = [163, 163, 163];
      const border = [229, 229, 229];
      const softBg = [250, 250, 250];
      const brand = [249, 115, 22];
      const success = [22, 163, 74];
      const successBg = [220, 252, 231];

      // Data
      const paymentDate = formatDate(invoiceData.paidAt || invoiceData.updatedAt || new Date());
      const currency = invoiceData.currency || 'USD';
      const amount = parseFloat(invoiceData.amount || invoiceData.price || invoiceData.total || 0) || 0;
      const subtotal = parseFloat(invoiceData.subtotal || amount) || 0;
      const taxAmount = parseFloat(invoiceData.tax || invoiceData.taxAmount || 0) || 0;
      const total = amount;
      const clientEmail = getEmailFromString(invoiceData.clientEmail || invoiceData.email || '');
      const clientName = invoiceData.clientName || invoiceData.client || invoiceData.customer || 'Client';
      const projectName = invoiceData.project || invoiceData.projectName || 'Professional Services';
      const fromName = invoiceData.fromName || '';
      const receiptId = (invoiceId || '').substring(0, 12).toUpperCase();

      // Helpers
      const fmtMoney = (n) => `${currency} ${Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      const setText = (size, weight = 'normal', color = ink) => {
        doc.setFontSize(size);
        doc.setFont('helvetica', weight);
        doc.setTextColor(color[0], color[1], color[2]);
      };
      const drawText = (text, x, y, opts = {}) => {
        const maxWidth = opts.maxWidth;
        let value = text == null ? '' : String(text);
        if (maxWidth) {
          const lines = doc.splitTextToSize(value, maxWidth);
          value = lines[0];
          if (lines.length > 1) {
            // ensure ellipsis fits
            while (value.length > 3 && doc.getTextWidth(value + '…') > maxWidth) {
              value = value.slice(0, -1);
            }
            value = value.trimEnd() + '…';
          }
        }
        doc.text(value, x, y, opts);
      };

      let yPos = margin + 4;

      // ── Header ──
      setText(11, 'bold', brand);
      doc.text('KLORAH', margin, yPos);
      setText(10, 'normal', muted);
      doc.text('Payment Receipt', pageWidth - margin, yPos, { align: 'right' });
      yPos += 28;

      // Title block
      setText(26, 'bold', ink);
      doc.text('Payment Received', margin, yPos);
      yPos += 18;
      setText(11, 'normal', subtle);
      doc.text(`Receipt #${receiptId}  ·  ${paymentDate}`, margin, yPos);

      // PAID badge (right aligned to top title row)
      const badgeText = 'PAID';
      setText(10, 'bold', success);
      const badgeTextW = doc.getTextWidth(badgeText);
      const badgeW = badgeTextW + 24;
      const badgeH = 22;
      const badgeX = pageWidth - margin - badgeW;
      const badgeY = yPos - 30;
      doc.setFillColor(successBg[0], successBg[1], successBg[2]);
      doc.roundedRect(badgeX, badgeY, badgeW, badgeH, 11, 11, 'F');
      setText(10, 'bold', success);
      doc.text(badgeText, badgeX + badgeW / 2, badgeY + 14, { align: 'center' });

      yPos += 28;

      // Divider
      doc.setDrawColor(border[0], border[1], border[2]);
      doc.setLineWidth(0.5);
      doc.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 24;

      // ── Bill To / From two columns ──
      const colGap = 24;
      const colWidth = (contentWidth - colGap) / 2;
      const leftX = margin;
      const rightX = margin + colWidth + colGap;
      const colStartY = yPos;

      setText(9, 'bold', muted);
      doc.text('BILLED TO', leftX, yPos);
      let lY = yPos + 16;
      setText(13, 'bold', ink);
      drawText(clientName, leftX, lY, { maxWidth: colWidth });
      lY += 16;
      if (clientEmail) {
        setText(10, 'normal', subtle);
        drawText(clientEmail, leftX, lY, { maxWidth: colWidth });
        lY += 14;
      }

      let rY = yPos + 16;
      if (fromName) {
        setText(9, 'bold', muted);
        doc.text('FROM', rightX, yPos);
        setText(13, 'bold', ink);
        drawText(fromName, rightX, rY, { maxWidth: colWidth });
        rY += 16;
      } else {
        setText(9, 'bold', muted);
        doc.text('PAYMENT DATE', rightX, yPos);
        setText(13, 'bold', ink);
        drawText(paymentDate, rightX, rY, { maxWidth: colWidth });
        rY += 16;
      }

      yPos = Math.max(lY, rY, colStartY + 40) + 16;

      // ── Items table ──
      const lineItems = invoiceData.lineItems || invoiceData.items || [];
      const tableBody = (lineItems && lineItems.length > 0)
        ? lineItems.map(item => [
            String(item.description || item.name || 'Service'),
            String(item.quantity ?? 1),
            fmtMoney(item.rate || item.price || 0),
            fmtMoney(item.amount || item.total || ((item.quantity || 1) * (item.rate || item.price || 0))),
          ])
        : [[projectName, '1', fmtMoney(amount), fmtMoney(amount)]];

      autoTable(doc, {
        startY: yPos,
        head: [['Description', 'Qty', 'Rate', 'Amount']],
        body: tableBody,
        margin: { left: margin, right: margin },
        tableWidth: contentWidth,
        theme: 'plain',
        styles: {
          font: 'helvetica',
          fontSize: 10,
          cellPadding: { top: 10, right: 10, bottom: 10, left: 10 },
          textColor: ink,
          lineColor: border,
          lineWidth: 0,
          overflow: 'linebreak',
          valign: 'middle',
        },
        headStyles: {
          fillColor: softBg,
          textColor: subtle,
          fontStyle: 'bold',
          fontSize: 9,
          cellPadding: { top: 8, right: 10, bottom: 8, left: 10 },
        },
        columnStyles: {
          0: { halign: 'left', cellWidth: contentWidth * 0.5 },
          1: { halign: 'center', cellWidth: contentWidth * 0.1, textColor: subtle },
          2: { halign: 'right', cellWidth: contentWidth * 0.2, textColor: subtle },
          3: { halign: 'right', cellWidth: contentWidth * 0.2, fontStyle: 'bold' },
        },
        didDrawCell: (data) => {
          if (data.section === 'body' && data.column.index === 0) {
            // bottom border per row
            const x1 = margin;
            const x2 = pageWidth - margin;
            const y = data.cell.y + data.cell.height;
            doc.setDrawColor(border[0], border[1], border[2]);
            doc.setLineWidth(0.5);
            doc.line(x1, y, x2, y);
          }
        },
      });

      yPos = doc.lastAutoTable.finalY + 20;

      // ── Summary (right-aligned, full safe width) ──
      const summaryWidth = 260;
      const summaryX = pageWidth - margin - summaryWidth;
      const labelX = summaryX + 16;
      const valueX = summaryX + summaryWidth - 16;
      const rowH = 22;

      const summaryRows = [];
      if (taxAmount > 0) {
        summaryRows.push({ label: 'Subtotal', value: fmtMoney(subtotal), strong: false });
        const taxLabel = invoiceData.taxType === 'percentage' ? `Tax (${invoiceData.taxRate || 0}%)` : 'Tax';
        summaryRows.push({ label: taxLabel, value: fmtMoney(taxAmount), strong: false });
      }

      // Draw subtotal/tax rows
      summaryRows.forEach((row) => {
        setText(10, 'normal', subtle);
        doc.text(row.label, labelX, yPos + 14);
        setText(10, 'normal', ink);
        doc.text(row.value, valueX, yPos + 14, { align: 'right' });
        yPos += rowH;
      });

      if (summaryRows.length > 0) {
        doc.setDrawColor(border[0], border[1], border[2]);
        doc.setLineWidth(0.5);
        doc.line(labelX, yPos + 2, valueX, yPos + 2);
        yPos += 10;
      }

      // Total Paid row — emphasized box
      const totalBoxH = 56;
      doc.setFillColor(softBg[0], softBg[1], softBg[2]);
      doc.roundedRect(summaryX, yPos, summaryWidth, totalBoxH, 10, 10, 'F');
      setText(11, 'bold', subtle);
      doc.text('TOTAL PAID', labelX, yPos + 22);
      // Auto-shrink total to fit
      const maxValueWidth = summaryWidth - 32 - doc.getTextWidth('TOTAL PAID') - 8;
      let totalFontSize = 20;
      setText(totalFontSize, 'bold', success);
      while (doc.getTextWidth(fmtMoney(total)) > maxValueWidth && totalFontSize > 12) {
        totalFontSize -= 1;
        setText(totalFontSize, 'bold', success);
      }
      doc.text(fmtMoney(total), valueX, yPos + 38, { align: 'right' });
      yPos += totalBoxH + 24;

      // ── Payment Method strip ──
      const methodLabel = 'Payment Method';
      const methodValue = invoiceData.paymentMethod || 'Secure Online Payment';
      setText(9, 'bold', muted);
      doc.text(methodLabel.toUpperCase(), margin, yPos);
      setText(11, 'normal', ink);
      drawText(methodValue, margin, yPos + 16, { maxWidth: contentWidth });
      yPos += 36;

      // ── Footer ──
      const footerY = pageHeight - margin - 14;
      doc.setDrawColor(border[0], border[1], border[2]);
      doc.setLineWidth(0.5);
      doc.line(margin, footerY - 22, pageWidth - margin, footerY - 22);
      setText(10, 'normal', subtle);
      doc.text('Thank you for your business.', margin, footerY - 6);
      setText(9, 'normal', muted);
      doc.text('Powered by Klorah · klorah.app', pageWidth - margin, footerY - 6, { align: 'right' });

      // Save
      const dateStr = new Date().toISOString().split('T')[0];
      const clientSlug = (invoiceData.client || invoiceData.clientName || 'client')
        .toString()
        .replace(/[^a-zA-Z0-9]/g, '-')
        .toLowerCase()
        .substring(0, 24);
      const filename = `klorah-receipt-${(invoiceId || '').substring(0, 8)}-${clientSlug}-${dateStr}.pdf`;
      doc.save(filename);
    } catch (error) {
      console.error('Error generating receipt PDF:', error);
      alert('Failed to generate receipt PDF. Please try again.');
    }
  };

  // Function to download receipt
  const downloadReceipt = () => {
    generateReceiptPDF();
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-[#0A0A0A] py-8 px-4 transition-colors">
      <div className="max-w-2xl mx-auto space-y-6">
            {/* Success Header */}
            <div className="text-center">
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.2 }}
                className="w-12 h-12 mx-auto mb-4 bg-emerald-50 dark:bg-emerald-500/10 rounded-xl flex items-center justify-center ring-1 ring-emerald-100 dark:ring-emerald-500/20"
              >
                <svg className="w-6 h-6 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-2xl font-semibold text-gray-900 dark:text-white mb-2 tracking-tight"
              >
                Payment Confirmed
              </motion.h1>

              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.4 }}
                className="text-sm text-gray-500 dark:text-gray-400"
              >
                Thank you for your payment
              </motion.p>
            </div>

            {/* Payout Security Review Banner */}
            {payoutInfo && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <PayoutDelayBanner
                  delayDays={payoutInfo.delayDays}
                  tierName={payoutInfo.tierName}
                />
              </motion.div>
            )}

            {/* Email Confirmation Status */}
            {emailStatus && emailStatus.confirmationEnabled && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
                className={`border rounded-2xl p-6 ${
                  emailSending 
                    ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700/50'
                    : emailError
                    ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700/50'
                    : emailStatus.sent
                    ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-700/50'
                    : 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-700/50'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      emailSending
                        ? 'bg-amber-100 dark:bg-amber-800/30'
                        : emailError
                        ? 'bg-red-100 dark:bg-red-800/30'
                        : emailStatus.sent
                        ? 'bg-blue-100 dark:bg-blue-800/30'
                        : 'bg-gray-100 dark:bg-gray-800/30'
                    }`}>
                      {emailSending ? (
                        <div className="w-5 h-5 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" />
                      ) : emailError ? (
                        <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      ) : emailStatus.sent ? (
                        <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className={`font-semibold mb-2 ${
                      emailSending
                        ? 'text-amber-900 dark:text-amber-100'
                        : emailError
                        ? 'text-red-900 dark:text-red-100'
                        : emailStatus.sent
                        ? 'text-blue-900 dark:text-blue-100'
                        : 'text-gray-900 dark:text-gray-100'
                    }`}>
                      {emailSending ? (
                        '📧 Sending Confirmation Email...'
                      ) : emailError ? (
                        '❌ Email Sending Failed'
                      ) : emailStatus.sent ? (
                        '📧 Confirmation Email Sent'
                      ) : (
                        '📧 Email Confirmation'
                      )}
                    </h3>
                    <p className={`text-sm leading-relaxed mb-3 ${
                      emailSending
                        ? 'text-amber-700 dark:text-amber-300'
                        : emailError
                        ? 'text-red-700 dark:text-red-300'
                        : emailStatus.sent
                        ? 'text-blue-700 dark:text-blue-300'
                        : 'text-gray-700 dark:text-gray-300'
                    }`}>
                      {emailSending ? (
                        `Preparing your ${emailStatus.style} confirmation email...`
                      ) : emailError ? (
                        `Failed to send confirmation email: ${emailError}`
                      ) : emailStatus.sent ? (
                        `A ${emailStatus.style} confirmation email has been sent to your inbox with payment details and receipt.`
                      ) : (
                        'Confirmation email is being prepared...'
                      )}
                    </p>
                    {emailStatus.notifyCreator && !emailError && (
                      <div className="flex items-center gap-2">
                        <div className={`w-1.5 h-1.5 rounded-full ${
                          emailSending
                            ? 'bg-amber-500'
                            : emailStatus.sent
                            ? 'bg-blue-500'
                            : 'bg-gray-500'
                        }`}></div>
                        <span className={`text-xs font-medium ${
                          emailSending
                            ? 'text-amber-600 dark:text-amber-400'
                            : emailStatus.sent
                            ? 'text-blue-600 dark:text-blue-400'
                            : 'text-gray-600 dark:text-gray-400'
                        }`}>
                          {emailSending ? 'Also notifying invoice creator...' : 'Payment notification also sent to invoice creator'}
                        </span>
                      </div>
                    )}
                    {emailError && (
                      <motion.button
                        onClick={() => window.location.reload()}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="mt-3 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
                      >
                        Retry Email Sending
                      </motion.button>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Client Feedback Survey - Only show if survey exists or is being created */}
            {(existingSurvey || surveyStatus.created || surveyCreating || surveyError) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
                className={`border rounded-2xl p-6 ${
                  surveyCreating 
                    ? 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700/50'
                    : surveyError
                    ? 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700/50'
                    : surveyStatus.created
                    ? 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-700/50'
                    : 'bg-gray-50 dark:bg-gray-900/20 border-gray-200 dark:border-gray-700/50'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-shrink-0">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                      surveyCreating
                        ? 'bg-amber-100 dark:bg-amber-800/30'
                        : surveyError
                        ? 'bg-red-100 dark:bg-red-800/30'
                        : surveyStatus.created
                        ? 'bg-purple-100 dark:bg-purple-800/30'
                        : 'bg-gray-100 dark:bg-gray-800/30'
                    }`}>
                      {surveyCreating ? (
                        <div className="w-5 h-5 border-2 border-amber-600 border-t-transparent rounded-full animate-spin" />
                      ) : surveyError ? (
                        <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      ) : surveyStatus.created ? (
                        <svg className="w-5 h-5 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      ) : (
                        <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                      )}
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className={`font-semibold mb-2 ${
                      surveyCreating
                        ? 'text-amber-900 dark:text-amber-100'
                        : surveyError
                        ? 'text-red-900 dark:text-red-100'
                        : surveyStatus.created
                        ? 'text-purple-900 dark:text-purple-100'
                        : 'text-gray-900 dark:text-gray-100'
                    }`}>
                      {surveyCreating ? (
                        '📝 Creating Feedback Survey...'
                      ) : surveyError ? (
                        '❌ Survey Creation Failed'
                      ) : surveyStatus.created ? (
                        '📝 Feedback Survey Ready'
                      ) : (
                        '📝 Client Feedback Survey'
                      )}
                    </h3>
                    <p className={`text-sm leading-relaxed mb-4 ${
                      surveyCreating
                        ? 'text-amber-700 dark:text-amber-300'
                        : surveyError
                        ? 'text-red-700 dark:text-red-300'
                        : surveyStatus.created
                        ? 'text-purple-700 dark:text-purple-300'
                        : 'text-gray-700 dark:text-gray-300'
                    }`}>
                      {surveyCreating ? (
                        'Setting up a feedback survey to gather client insights about your photography service...'
                      ) : surveyError ? (
                        `Failed to create feedback survey: ${surveyError}`
                      ) : surveyStatus.created ? (
                        `A feedback survey has been created for ${surveyStatus.clientName || 'your client'}. They can provide valuable insights about their experience with your photography service.`
                      ) : (
                        `Create a feedback survey for ${surveyStatus.clientName || 'your client'} to gather valuable insights about their experience with your photography service.`
                      )}
                    </p>
                    
                    {!surveyStatus.created && !surveyCreating && (
                      <motion.button
                        onClick={handleSurveyPrompt}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="px-4 py-2 bg-brand hover:bg-brand-hover text-white text-sm font-medium rounded-lg transition-colors"
                      >
                        Create Feedback Survey
                      </motion.button>
                    )}
                    
                    {surveyStatus.created && existingSurvey && (
                      <div className="space-y-3">
                        <div className="flex items-center gap-3">
                          <div className="w-1.5 h-1.5 rounded-full bg-purple-500"></div>
                          <span className="text-xs font-medium text-purple-600 dark:text-purple-400">
                            Survey link: klorah.app/survey-response/{existingSurvey.id}
                          </span>
                        </div>
                        <div className="flex flex-col sm:flex-row gap-3">
                          <motion.a
                            href={`/survey-response/${existingSurvey.id}`}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="inline-block px-6 py-3 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-colors"
                          >
                            Take Survey Now
                          </motion.a>
                          {redirectCountdown && redirectCountdown > 0 && (
                            <div className="flex items-center gap-2 px-4 py-3 bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 rounded-lg text-sm">
                              <div className="w-4 h-4 border-2 border-purple-600 border-t-transparent rounded-full animate-spin"></div>
                              <span>Auto-redirecting in {redirectCountdown}s</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {surveyError && (
                      <motion.button
                        onClick={handleSurveyPrompt}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        className="mt-3 px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-sm font-medium rounded-lg transition-colors"
                      >
                        Retry Survey Creation
                      </motion.button>
                    )}
                  </div>
                </div>
              </motion.div>
            )}

            {/* Payment Receipt */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="bg-white dark:bg-[#111111] border border-gray-200 dark:border-[#1F1F1F] rounded-3xl p-6 sm:p-8 shadow-sm dark:shadow-none"
            >
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-8">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white tracking-tight">
                  Payment Receipt
                </h2>
                <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-500 font-mono break-all">
                  ID {invoiceId}
                </span>
              </div>

              <div className="space-y-1">
                {/* Project/Service */}
                <div className="flex items-center justify-between gap-4 py-3">
                  <span className="text-gray-600 dark:text-gray-400 font-medium text-sm shrink-0">Service</span>
                  <span className="text-gray-900 dark:text-white font-semibold text-right truncate">
                    {invoiceData.project || invoiceData.projectName || 'Professional Services'}
                  </span>
                </div>

                {/* Business Info */}
                {invoiceData.fromName && (
                  <div className="flex items-center justify-between gap-4 py-3 border-t border-gray-100 dark:border-[#1F1F1F]">
                    <span className="text-gray-600 dark:text-gray-400 font-medium text-sm shrink-0">From</span>
                    <span className="text-gray-900 dark:text-white font-semibold text-right truncate">
                      {invoiceData.fromName}
                    </span>
                  </div>
                )}

                {/* Client Info */}
                {(invoiceData.clientName || invoiceData.client) && (
                  <div className="flex items-center justify-between gap-4 py-3 border-t border-gray-100 dark:border-[#1F1F1F]">
                    <span className="text-gray-600 dark:text-gray-400 font-medium text-sm shrink-0">Client</span>
                    <span className="text-gray-900 dark:text-white font-semibold text-right truncate">
                      {invoiceData.clientName || invoiceData.client}
                    </span>
                  </div>
                )}

                {/* Payment Date */}
                <div className="flex items-center justify-between gap-4 py-3 border-t border-gray-100 dark:border-[#1F1F1F]">
                  <span className="text-gray-600 dark:text-gray-400 font-medium text-sm shrink-0">Payment Date</span>
                  <span className="text-gray-900 dark:text-white font-semibold text-right">
                    {formatDate(invoiceData.paidAt || invoiceData.updatedAt || new Date())}
                  </span>
                </div>

                {/* Payment Method */}
                <div className="flex items-center justify-between gap-4 py-3 border-t border-gray-100 dark:border-[#1F1F1F]">
                  <span className="text-gray-600 dark:text-gray-400 font-medium text-sm shrink-0">Payment Method</span>
                  <div className="flex items-center gap-2 min-w-0">
                    <svg className="w-4 h-4 text-gray-400 dark:text-gray-500 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                    </svg>
                    <span className="text-gray-900 dark:text-white font-semibold truncate">
                      {invoiceData.paymentMethod || 'Secure Online Payment'}
                    </span>
                  </div>
                </div>

                {/* Total Paid — highlighted */}
                <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-gray-50 dark:bg-[#1A1A1A] border border-gray-100 dark:border-[#262626] px-5 py-4">
                  <span className="text-gray-900 dark:text-white font-bold">Total Paid</span>
                  <span className="text-2xl font-bold text-emerald-600 dark:text-emerald-400 font-mono whitespace-nowrap">
                    {invoiceData.currency || 'USD'} {(invoiceData.amount || invoiceData.price || 0).toFixed(2)}
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Action Buttons */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <motion.button
                onClick={downloadReceipt}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-white dark:bg-[#1A1A1A] hover:bg-gray-50 dark:hover:bg-[#222222] text-gray-900 dark:text-white font-medium rounded-xl border border-gray-200 dark:border-[#262626] transition-all duration-200"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Download Receipt
              </motion.button>

              <motion.button
                onClick={() => window.location.href = 'https://klorah.app'}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex-1 px-6 py-3 bg-brand hover:bg-brand-hover text-white font-medium rounded-xl transition-all duration-200 shadow-sm shadow-brand/10"
              >
                Continue to Klorah
              </motion.button>
            </motion.div>

            {/* Footer */}
            <p className="text-xs text-gray-400 dark:text-gray-600 text-center">
              This receipt was generated by{' '}
              <a href="https://klorah.app" className="text-brand hover:text-brand-hover font-medium transition-colors">
                Klorah
              </a>
            </p>
      </div>

      {/* Survey Creation Modal */}
      <FeedbackSurveyModal
        isOpen={showSurveyModal}
        onClose={() => setShowSurveyModal(false)}
        onSubmit={handleCreateSurvey}
        isLoading={surveyCreating}
        prefilledData={{
          clientName: surveyStatus?.clientName || '',
          clientEmail: surveyStatus?.clientEmail || '',
          projectName: invoiceData?.project || invoiceData?.projectName || ''
        }}
      />
    </div>
  );
}
